import type { ClientField, ClientForm, ClientPerson } from './types';

/**
 * QUESTIONS THAT ARE REALLY ONE QUESTION
 * ======================================
 *
 * MP can only describe a form as a flat list of fields, so two things that
 * are one thing to the person answering arrive as several rows:
 *
 *   the emergency contact  name, phone and relationship — three boxes for
 *                          one person, asked every year, when the church
 *                          already knows the answer
 *   the waiver             a page of legal text in an Instructions field,
 *                          then "I agree", then a signature box, all three
 *                          rendered as if they were unrelated questions
 *
 * Both are recognised here and rendered as one control each, the way the
 * student retreats and football sites already do them. The answers still go
 * back to MP field by field, because the form response is what reports read.
 *
 * Recognised by configuration first and by label second — the same fallback
 * the group-join form uses for its campus question. A form that names these
 * things differently simply gets the plain fields, which is what it had.
 */

export interface EmergencyFields {
  name: ClientField;
  phone: ClientField | null;
  relationship: ClientField | null;
}

export interface WaiverFields {
  /** The Instructions field holding the text itself. */
  doc: ClientField;
  agree: ClientField | null;
  signature: ClientField | null;
}

const is = (f: ClientField, re: RegExp) => re.test(f.label ?? '') || re.test(f.title ?? '');

/** The three fields that are one person. Null unless at least a name is asked. */
export function emergencyFieldsOf(form: ClientForm | null): EmergencyFields | null {
  if (!form) return null;
  const mine = form.fields.filter(
    (f) => !f.hidden && (f.component === 'EmergencyContact' || is(f, /emergency contact/i))
  );
  if (!mine.length) return null;
  const phone = mine.find((f) => f.kind === 'phone' || is(f, /phone|mobile|number/i)) ?? null;
  const relationship = mine.find((f) => is(f, /relationship|relation\b/i)) ?? null;
  const name = mine.find((f) => f !== phone && f !== relationship) ?? null;
  return name ? { name, phone, relationship } : null;
}

/**
 * A page of legal text, what it wants ticked, and where it is signed.
 *
 * TWO SHAPES, BECAUSE STAFF BUILD THEM BOTH WAYS.
 *
 * The one this was written for is a long Instructions field followed by "I
 * agree" and a signature box. The other is a single required checkbox whose
 * label IS the whole document — Woodside's baptism forms are this, and until
 * a field said `component: "Waiver"` there was no way to tell that checkbox
 * from any other. It was drawn as an ordinary tick in the middle of the
 * questions, which is exactly where a thing somebody is agreeing to should
 * not be.
 *
 * A tagged field of any other kind is the whole document and its own
 * agreement: it is the text, and ticking it is the act.
 */
export function waiverOf(form: ClientForm | null): WaiverFields | null {
  if (!form) return null;
  const tagged = form.fields.find((f) => !f.hidden && f.component === 'Waiver');
  if (tagged && tagged.kind !== 'instructions') {
    return { doc: tagged, agree: tagged, signature: null };
  }
  const doc = form.fields.find(
    (f) =>
      !f.hidden &&
      f.kind === 'instructions' &&
      (f.component === 'Waiver' || (f.label ?? '').length > 400)
  );
  if (!doc) return null;
  const after = form.fields.slice(form.fields.indexOf(doc) + 1);
  const agree = after.find((f) => is(f, /^i agree|i consent|i accept/i)) ?? null;
  const signature = after.find((f) => is(f, /signature|sign here|type your name/i)) ?? null;
  // Length alone is not a waiver. What Matters' 800-character lunch note
  // ("LUNCH WILL BE CATERED BY PANERA...") put a Sign step on a form with
  // nothing to sign; an untagged block counts only when something follows
  // it to agree or sign with.
  if (doc.component !== 'Waiver' && !agree && !signature) return null;
  return { doc, agree, signature };
}

/**
 * The question that is really about somebody's health.
 *
 * "Anything we should know?" with allergies in the helper text is a medical
 * question wearing a memo box: what comes back is a sentence, and a sentence
 * cannot be reported on, checked against an EpiPen's expiry, or carried to
 * next year's form. Recognised so it can be asked as the card the profile
 * widget files.
 */
export function medicalFieldOf(form: ClientForm | null): ClientField | null {
  if (!form) return null;
  return (
    form.fields.find(
      (f) =>
        !f.hidden &&
        !f.parentId &&
        (f.component === 'Medical' ||
          (f.kind === 'memo' &&
            /allerg|medical|medication|health|epipen|inhaler/i.test(
              `${f.label ?? ''} ${f.title ?? ''} ${f.helperText ?? ''}`
            )))
    ) ?? null
  );
}

/** Every field the two controls above swallow, so the list skips them. */
export function groupedIds(form: ClientForm | null): Set<number> {
  const out = new Set<number>();
  const e = emergencyFieldsOf(form);
  if (e) for (const f of [e.name, e.phone, e.relationship]) if (f) out.add(f.id);
  const w = waiverOf(form);
  if (w) for (const f of [w.doc, w.agree, w.signature]) if (f) out.add(f.id);
  return out;
}

/**
 * What the church can already answer for somebody, as answers.
 *
 * Only the emergency contact for now, and only the name and number: the
 * relationship on file is to the household, not to the person being
 * registered — Sarah is the viewer's wife and her son's mother — so that one
 * is left to be picked rather than guessed wrong.
 */
export function seedAnswers(
  form: ClientForm | null,
  person: ClientPerson | undefined
): Record<string, string> {
  const out: Record<string, string> = {};
  const fields = emergencyFieldsOf(form);
  if (!fields || !person?.emergency) return out;
  out[fields.name.id] = person.emergency.name;
  if (fields.phone && person.emergency.phone)
    out[fields.phone.id] = prettyPhone(person.emergency.phone);
  return out;
}

/** (586) 555-1234 from anything with ten digits in it. */
export function prettyPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length < 4) return ten;
  if (ten.length < 7) return `(${ten.slice(0, 3)}) ${ten.slice(3)}`;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
}

export const phoneOk = (raw: string) => raw.replace(/\D/g, '').replace(/^1/, '').length === 10;
